import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ImagePicker from "@/components/admin/ImagePicker";
import { Plus, Trash2, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

export default function BlogManager() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const r = await api.get("/content/blog");
    setItems(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const editing = useMemo(
    () => items.find((p) => p.id === editingId) || null,
    [items, editingId]
  );

  const upd = (id, field, val) =>
    setItems(items.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  const add = async () => {
    const baseTitle = "Untitled post";
    let slug = "untitled-post";
    let counter = 1;
    const existingSlugs = new Set(items.map((p) => p.slug));
    while (existingSlugs.has(slug)) {
      counter += 1;
      slug = `untitled-post-${counter}`;
    }
    try {
      const r = await api.post("/content/blog", {
        title: baseTitle,
        slug,
        cover_url: "",
        excerpt: "",
        content: "<p>Write your story here…</p>",
        author: "Aiyavi Team",
        enabled: false,
        order: items.length,
      });
      toast.success("Post created");
      await load();
      setEditingId(r.data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const save = async (item) => {
    if (!item.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    const slug = (item.slug || "").trim() || slugify(item.title);
    try {
      await api.put(`/content/blog/${item.id}`, { ...item, slug });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/content/blog/${id}`);
      toast.success("Removed");
      if (editingId === id) setEditingId(null);
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Manage</div>
          <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">Blog</h1>
          <p className="text-earth-600 mt-2 max-w-xl">
            Write stories, host guides and updates. Posts appear at <code className="text-[#FF6B35]">/blog</code>.
            Content accepts simple HTML (h2, h3, p, ul, li, blockquote, img, a).
          </p>
        </div>
        <Button
          onClick={add}
          className="bg-sage-500 text-white hover:bg-sage-600 rounded-full"
          data-testid="add-blog-btn"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New post
        </Button>
      </div>

      {!editing && (
        <div className="grid gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              className="bg-white rounded-2xl border border-earth-100 p-5 flex gap-4 items-center"
              data-testid={`blog-row-${p.id}`}
            >
              {p.cover_url ? (
                <img
                  src={p.cover_url}
                  alt={p.title}
                  className="w-24 h-24 rounded-xl object-cover border border-earth-100 flex-shrink-0"
                />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-earth-100 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-earth-500">
                  <span>{p.date}</span>
                  <span>·</span>
                  <span className={p.enabled ? "text-sage-600" : "text-earth-400"}>
                    {p.enabled ? "Published" : "Draft"}
                  </span>
                </div>
                <div className="font-serif text-xl text-earth-900 mt-1 truncate">
                  {p.title}
                </div>
                <div className="text-xs text-earth-500 mt-1 truncate">/blog/{p.slug}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {p.enabled && (
                  <a
                    href={`/blog/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full flex items-center justify-center text-earth-600 hover:bg-earth-50"
                    data-testid={`blog-view-${p.id}`}
                  >
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                )}
                <Button
                  size="sm"
                  onClick={() => setEditingId(p.id)}
                  className="bg-earth-900 text-white hover:bg-earth-800 rounded-full"
                  data-testid={`blog-edit-${p.id}`}
                >
                  Edit
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => del(p.id)}
                  data-testid={`blog-del-${p.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-600" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="p-8 text-center text-earth-500">
              No posts yet — click <strong>New post</strong> to write your first story.
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="bg-white rounded-2xl border border-earth-100 p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setEditingId(null)}
              className="text-sm text-earth-600 hover:text-earth-900"
              data-testid="blog-back-list"
            >
              ← Back to all posts
            </button>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-sm">
                <Switch
                  checked={editing.enabled !== false}
                  onCheckedChange={(v) => upd(editing.id, "enabled", v)}
                  data-testid="blog-enabled-switch"
                />
                <span className="text-earth-700">
                  {editing.enabled !== false ? "Published" : "Draft"}
                </span>
              </div>
              <Button
                onClick={() => save(editing)}
                className="bg-earth-900 text-white hover:bg-earth-800 rounded-full"
                data-testid="blog-save-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Save
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 sm:col-span-8">
              <label className="text-xs text-earth-500 uppercase tracking-[0.22em]">Title</label>
              <Input
                className="mt-1"
                value={editing.title}
                onChange={(e) => {
                  upd(editing.id, "title", e.target.value);
                  // auto-sync slug if it matches old slugified title
                  if (!editing.slug || editing.slug === slugify(editing.title)) {
                    upd(editing.id, "slug", slugify(e.target.value));
                  }
                }}
                placeholder="Post title"
                data-testid="blog-title-input"
              />
            </div>
            <div className="col-span-7 sm:col-span-3">
              <label className="text-xs text-earth-500 uppercase tracking-[0.22em]">Slug (URL)</label>
              <Input
                className="mt-1"
                value={editing.slug}
                onChange={(e) => upd(editing.id, "slug", slugify(e.target.value))}
                placeholder="post-slug"
                data-testid="blog-slug-input"
              />
            </div>
            <div className="col-span-5 sm:col-span-1">
              <label className="text-xs text-earth-500 uppercase tracking-[0.22em]">Order</label>
              <Input
                type="number"
                className="mt-1"
                value={editing.order}
                onChange={(e) => upd(editing.id, "order", Number(e.target.value))}
              />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 sm:col-span-6">
              <label className="text-xs text-earth-500 uppercase tracking-[0.22em]">Date</label>
              <Input
                type="date"
                className="mt-1"
                value={editing.date || ""}
                onChange={(e) => upd(editing.id, "date", e.target.value)}
              />
            </div>
            <div className="col-span-12 sm:col-span-6">
              <label className="text-xs text-earth-500 uppercase tracking-[0.22em]">Author</label>
              <Input
                className="mt-1"
                value={editing.author || ""}
                onChange={(e) => upd(editing.id, "author", e.target.value)}
                placeholder="Aiyavi Team"
              />
            </div>
          </div>

          <ImagePicker
            label="Cover image"
            value={editing.cover_url}
            onChange={(v) => upd(editing.id, "cover_url", v)}
            testId="blog-cover-picker"
            aspect="aspect-video"
          />

          <div>
            <label className="text-xs text-earth-500 uppercase tracking-[0.22em]">Excerpt</label>
            <Textarea
              rows={2}
              className="mt-1"
              value={editing.excerpt || ""}
              onChange={(e) => upd(editing.id, "excerpt", e.target.value)}
              placeholder="A short, evocative summary (1–2 lines)."
              data-testid="blog-excerpt-input"
            />
          </div>

          <div>
            <label className="text-xs text-earth-500 uppercase tracking-[0.22em]">
              Content (HTML)
            </label>
            <Textarea
              rows={16}
              className="mt-1 font-mono text-sm"
              value={editing.content || ""}
              onChange={(e) => upd(editing.id, "content", e.target.value)}
              placeholder="<p>Your story…</p>"
              data-testid="blog-content-input"
            />
            <p className="text-xs text-earth-500 mt-1">
              Tip: wrap paragraphs in <code>&lt;p&gt;</code>, use <code>&lt;h2&gt;</code>/
              <code>&lt;h3&gt;</code> for sections.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
